/**
 * alerts.js — single source of truth for "is this drawing a price alert, and at
 * what price?".
 *
 * This predicate used to be duplicated per consumer, and the copies drifted: the
 * Telegram sync still matched the pre-uPlot CyberChart shape ({isAlert, alertPrice})
 * while the chart had long been writing {name:'alert', points:[{ts, price}]}. The
 * sync therefore always sent an empty alert list, which wiped the server-side
 * monitor's rows every 30s and silently killed every Telegram alert. Keep the shape
 * knowledge here so a future chart migration can only break it in one place.
 */

// Returns the alert's price level, or null when the drawing isn't a price alert.
export function alertPriceOf(d) {
  if (!d) return null;
  // Current shape (UplotChart → DrawingLayer).
  if (d.name === 'alert') {
    const p = d.points?.[0]?.price;
    return Number.isFinite(p) ? p : null;
  }
  // Legacy shape (CyberChart, pre-uPlot) — kept so alerts drawn before the
  // migration still fire.
  if (d.isAlert) {
    const p = d.alertPrice ?? d.points?.[0]?.value;
    return Number.isFinite(p) ? p : null;
  }
  return null;
}

// Flatten a DrawingContext store ({ [symbol]: Drawing[] }) into alert records.
export function collectAlertDrawings(drawings) {
  const out = [];
  for (const symbol in (drawings || {})) {
    for (const d of drawings[symbol] || []) {
      const price = alertPriceOf(d);
      if (price == null) continue;
      out.push({ id: d.id, symbol, price, timeframe: d.timeframe || null });
    }
  }
  return out;
}

/**
 * Fold live prices into crossing events, mirroring the server-side monitor:
 * remember which side of its level each alert sits on and fire when that flips.
 *
 * A CROSS, not a touch — and the first sighting only seeds the baseline, so
 * placing a line near price can't instantly fire it.
 *
 * @param alerts  records from collectAlertDrawings()
 * @param priceOf (symbol) => number|null — live price lookup
 * @param sides   caller-owned Map(id → 'above'|'below'), mutated in place so the
 *                baseline survives across ticks
 * @returns the alerts that just crossed, each with the price that crossed it
 */
export function detectAlertCrossings(alerts, priceOf, sides) {
  const fired = [];
  const seen = new Set();
  for (const a of alerts || []) {
    seen.add(a.id);
    const price = priceOf(a.symbol);
    if (!Number.isFinite(price) || price <= 0) continue;
    const side = price >= a.price ? 'above' : 'below';
    const prev = sides.get(a.id);
    if (!prev) { sides.set(a.id, side); continue; }   // baseline only
    if (prev !== side) {
      sides.set(a.id, side);
      fired.push({ ...a, price });
    }
  }
  // Forget deleted alerts so a re-added id re-baselines instead of firing.
  for (const id of [...sides.keys()]) if (!seen.has(id)) sides.delete(id);
  return fired;
}
